/** C2 (GAP V2): Supplier Scorecards — ASR/ACD/PDD/MOS/margin + failure
 * breakdown per supplier, with window-over-window ASR trend.
 * Self-contained section: fetches /quality/scorecards on mount (and when
 * days changes). Mounted on RouteWorkspace (supplier context). */
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import { TrendingDown, TrendingUp, TrendingFlat } from "@mui/icons-material";
import apiClient from "../../api/client";

interface Scorecard {
  carrier: string;
  supplier: string;
  calls: number;
  answered: number;
  asr: number | null;
  asr_trend: number | null;
  acd_seconds: number | null;
  pdd_seconds: number | null;
  avg_mos_in: number | null;
  cost: number;
  revenue: number;
  margin: number;
  top_failures: { cause: string; count: number }[];
}

const daysOptions = [7, 30, 90];

export default function SupplierScorecards() {
  const [days, setDays] = useState(30);
  const [cards, setCards] = useState<Scorecard[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError("");
    try {
      const resp = await apiClient.get(`/quality/scorecards?days=${d}`);
      setCards(resp.data.scorecards ?? []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load scorecards");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const trendIcon = (trend: number | null) => {
    if (trend == null) return <TrendingFlat fontSize="small" />;
    if (trend < 0) return <TrendingDown fontSize="small" color="error" />;
    if (trend > 0) return <TrendingUp fontSize="small" color="success" />;
    return <TrendingFlat fontSize="small" />;
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Supplier Scorecards</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => v && setDays(v)}
          >
            {daysOptions.map((d) => (
              <ToggleButton key={d} value={d}>
                {d}d
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <CircularProgress size={24} sx={{ mb: 2 }} />}

        {!loading && cards && cards.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No carrier-attributed traffic in this window.
          </Typography>
        )}

        {!loading && cards && cards.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">Calls</TableCell>
                  <TableCell align="right">ASR</TableCell>
                  <TableCell align="center">Trend</TableCell>
                  <TableCell align="right">ACD</TableCell>
                  <TableCell align="right">PDD</TableCell>
                  <TableCell align="right">MOS</TableCell>
                  <TableCell align="right">Margin</TableCell>
                  <TableCell>Top failures</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cards.map((c) => (
                  <TableRow key={c.carrier}>
                    <TableCell>{c.supplier}</TableCell>
                    <TableCell align="right">{c.calls}</TableCell>
                    <TableCell align="right">
                      {c.asr != null ? `${c.asr}%` : "—"}
                    </TableCell>
                    <TableCell align="center">
                      {trendIcon(c.asr_trend)}
                    </TableCell>
                    <TableCell align="right">
                      {c.acd_seconds != null ? `${c.acd_seconds}s` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {c.pdd_seconds != null ? `${c.pdd_seconds}s` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {c.avg_mos_in ?? "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ color: c.margin < 0 ? "error.main" : undefined }}>
                      ${c.margin.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {c.top_failures.length > 0
                        ? c.top_failures.map((f) => `${f.cause} (${f.count})`).join(", ")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
